import React, { type ReactElement, useCallback } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import FormButtonsContainer from 'atom-components/form-buttons-container';
import CancelButton from 'atom-components/form-fields-for-hook-form/cancel-button';
import SubmitButton from 'atom-components/form-fields-for-hook-form/submit-button';
import { friendlyMarkStatus } from 'helpers/domain-specific/mark-status.helpers';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import { usePatchTestInstanceResponseMark } from 'services/assist-api/entities/test-instance-response-mark/hooks';
import {
  type ManageMarkingMark,
  MarkStatus
} from 'services/assist-api/entities/test-instance-response-mark/types';
import ActiveReviewer from './active-reviewer';
import { getOrganisationUserFromActiveReviewerId } from './active-reviewer.helpers';
import ReviewStatusSwitch from './review-status-switch';
import {
  type SetActiveReviewerFormValues,
  setActiveReviewerFormSchema
} from './set-active-reviewer-form-schema';

type Props = {
  mark: ManageMarkingMark;
  organisationUsers: BaseCustomerUser[];
  onClose: () => void;
};

export default function SetActiveReviewerForm({
  mark,
  organisationUsers,
  onClose
}: Props): ReactElement {
  const activeReviewerOrganisationUserId =
    getOrganisationUserFromActiveReviewerId(mark.activeReviewer?.id, organisationUsers)?.id || null;

  const methods = useForm({
    mode: 'onTouched',
    resolver: zodResolver(setActiveReviewerFormSchema(activeReviewerOrganisationUserId)),
    defaultValues: {
      organisationUserId: activeReviewerOrganisationUserId,
      furtherEscalated: false
    }
  });

  const { handleSubmit } = methods;

  /**
   * Determines the status a mark should be updated to when the form is submitted.
   * Marks are set to "In Review" by default.
   * The status is set to "Further Escalated" when the user toggles the switch on.
   * When an active reviewer is cleared, then the mark status is set back to "Needs Review".
   */
  function determineNewStatus(
    organisationUserID: number | null,
    furtherEscalated: boolean
  ): MarkStatus {
    if (organisationUserID) {
      return furtherEscalated ? MarkStatus.FurtherEscalated : MarkStatus.InReview;
    }
    return MarkStatus.NeedsReview;
  }

  const patchTestInstanceResponseMark = usePatchTestInstanceResponseMark({
    onSuccess: ({ data }) => {
      const organisationUser = getOrganisationUserFromActiveReviewerId(
        data.json.activeReviewer?.id,
        organisationUsers
      );

      if (organisationUser) {
        return toast.success(
          `Updated mark with status ${friendlyMarkStatus(data.json.status)} and new active reviewer: ${organisationUser?.user.firstname} ${organisationUser?.user.lastname}`
        );
      }

      return toast.success(
        `Updated mark with status ${friendlyMarkStatus(data.json.status)} and cleared the active reviewer.`
      );
    },
    onError: (error) => {
      toast.error(`Failed to update mark with status and new active reviewer: ${error.message}`);
    }
  });

  const updateMarkWithNewActiveReviewerAndStatus = useCallback(
    (newActiveReviewerId: number | null, newStatus: MarkStatus) => {
      patchTestInstanceResponseMark.mutate({
        id: mark.id,
        data: {
          activeReviewerId: newActiveReviewerId,
          status: newStatus
        }
      });
    },
    [mark.id, patchTestInstanceResponseMark]
  );
  const onSubmit: SubmitHandler<SetActiveReviewerFormValues> = (data) => {
    updateMarkWithNewActiveReviewerAndStatus(
      data.organisationUserId,
      determineNewStatus(data.organisationUserId, data.furtherEscalated)
    );
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <p>
          Please select a marking administrator to be the active reviewer of <b>mark #{mark.id}</b>.
        </p>
        <ReviewStatusSwitch organisationUsers={organisationUsers} />
        <ActiveReviewer organisationUsers={organisationUsers} />
        <FormButtonsContainer>
          <CancelButton onClick={onClose} />
          <SubmitButton>Submit</SubmitButton>
        </FormButtonsContainer>
      </form>
    </FormProvider>
  );
}
